// Dialog and Drawer on the native <dialog> element (DESIGN_SYSTEM §9.2): showModal() gives the
// focus trap, inert background and Esc; we restore focus to the trigger on close. No native alert,
// confirm or prompt is ever used. Danger buttons never take default focus; destructive confirms can
// require typing the object's name.
import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import { t } from "@ady/i18n";
import { cx } from "../lib";
import { Button, type ButtonVariant } from "./Button";
import { Field } from "./Field";
import { Icon } from "./Icon";
import { Input } from "./Input";

// The page behind an open modal never scrolls (owner, 2026-10-21): the wheel scrolls the modal when
// it has more to show, and otherwise nothing. A count keeps the lock while modals are stacked (the
// product picker over Create order). The scrollbar's width is padded back so the page doesn't shift.
let scrollLocks = 0;
function lockPageScroll(): () => void {
  const root = document.documentElement;
  if (scrollLocks++ === 0) {
    const gap = window.innerWidth - root.clientWidth;
    root.style.overflow = "hidden";
    if (gap > 0) root.style.paddingRight = `${gap}px`;
  }
  return () => {
    if (--scrollLocks === 0) {
      root.style.overflow = "";
      root.style.paddingRight = "";
    }
  };
}

function useModal(open: boolean, onClose: () => void, canClose: () => boolean = () => true) {
  const ref = useRef<HTMLDialogElement>(null);
  const returnFocus = useRef<HTMLElement | null>(null);
  useEffect(() => (open ? lockPageScroll() : undefined), [open]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) {
      returnFocus.current = document.activeElement as HTMLElement | null;
      el.showModal();
    } else if (!open && el.open) {
      el.close();
    }
  }, [open]);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onCancel = (e: Event) => {
      e.preventDefault(); // We control closing (dirty-form guard).
      if (canClose()) onClose();
    };
    const onClosed = () => {
      returnFocus.current?.focus?.();
      returnFocus.current = null;
    };
    el.addEventListener("cancel", onCancel);
    el.addEventListener("close", onClosed);
    return () => {
      el.removeEventListener("cancel", onCancel);
      el.removeEventListener("close", onClosed);
    };
  }, [onClose, canClose]);
  return ref;
}

export interface DialogAction {
  label: string;
  onClick: () => void;
  variant?: ButtonVariant;
  loading?: boolean;
  loadingLabel?: string;
  "data-testid"?: string;
}

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  /** States the action ("Cancel order 1042?"). */
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  /** The confirming action. Its label is the verb ("Cancel order"). */
  confirm?: DialogAction;
  /** The safe action ("Keep order"); defaults to Cancel and takes initial focus. */
  dismissLabel?: string;
  /** Destructive confirm: the user types this phrase to enable the confirm action. */
  typeToConfirm?: string;
  size?: "sm" | "md" | "lg";
  "data-testid"?: string;
}

export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  confirm,
  dismissLabel,
  typeToConfirm,
  size = "md",
  "data-testid": testId,
}: DialogProps) {
  const ref = useModal(open, onClose);
  const titleId = useId();
  const descId = useId();
  const [typed, setTyped] = useState("");
  const phraseOk = !typeToConfirm || typed.trim() === typeToConfirm;
  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);
  return (
    <dialog
      ref={ref}
      className={cx("ady-dialog", size === "sm" && "max-w-md", size === "lg" && "max-w-3xl")}
      aria-labelledby={titleId}
      aria-describedby={description ? descId : undefined}
      data-testid={testId}
    >
      {open ? (
        <div className="flex max-h-[inherit] flex-col">
          <header className="flex items-start gap-4 px-6 pt-6">
            <h2 id={titleId} className="flex-1 text-title-md font-semibold">
              {title}
            </h2>
            <Button
              variant="ghost"
              size="md"
              iconOnly
              icon="x"
              onClick={onClose}
              className="-me-2 -mt-1 text-fg-secondary"
            >
              {t("dialog.closeLabel")}
            </Button>
          </header>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {description ? (
              <p id={descId} className="text-md text-fg-secondary">
                {description}
              </p>
            ) : null}
            {children}
            {typeToConfirm ? (
              <Field
                className="mt-4"
                label={t("dialog.typeToConfirm", { phrase: typeToConfirm })}
                error={
                  typed && !phraseOk
                    ? t("dialog.typeMismatch", { phrase: typeToConfirm })
                    : undefined
                }
              >
                <Input
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  autoComplete="off"
                  userContent
                />
              </Field>
            ) : null}
          </div>
          <footer className="flex flex-col-reverse gap-3 border-t border-subtle px-6 py-4 sm:flex-row sm:justify-end">
            <Button
              variant="secondary"
              onClick={onClose}
              autoFocus
              data-testid={testId ? `${testId}-dismiss` : undefined}
            >
              {dismissLabel ?? t("common.cancel")}
            </Button>
            {confirm ? (
              <Button
                variant={confirm.variant ?? "primary"}
                onClick={confirm.onClick}
                loading={confirm.loading}
                loadingLabel={confirm.loadingLabel}
                availability={
                  phraseOk
                    ? undefined
                    : {
                        kind: "disabled",
                        reason: t("dialog.typeMismatch", { phrase: typeToConfirm ?? "" }),
                      }
                }
                data-testid={confirm["data-testid"]}
              >
                {confirm.label}
              </Button>
            ) : null}
          </footer>
        </div>
      ) : null}
    </dialog>
  );
}

export interface PopupProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name; the content draws its own visible heading. */
  label: string;
  children: ReactNode;
  /** xl (default): record popups such as order detail; lg: compact forms; md: pickers; sm: short lists. */
  size?: "sm" | "md" | "lg" | "xl";
  /** False: a click outside does not close it, only its own close button or Esc (long forms). */
  closeOnBackdrop?: boolean;
  "data-testid"?: string;
}

/** Wide centred record popup (D-038 order detail): no built-in chrome, closes on Esc or backdrop. */
export function Popup({
  open,
  onClose,
  label,
  children,
  size = "xl",
  closeOnBackdrop = true,
  "data-testid": testId,
}: PopupProps) {
  const ref = useModal(open, onClose);
  return (
    <dialog
      ref={ref}
      aria-label={label}
      className={cx(
        "ady-dialog ady-popup",
        size === "lg" && "ady-popup-lg",
        size === "md" && "ady-popup-md",
        size === "sm" && "ady-popup-sm",
      )}
      data-testid={testId}
      onClick={(e) => {
        if (closeOnBackdrop && e.target === e.currentTarget) onClose();
      }}
    >
      {/* relative: visually hidden labels (absolute) stay inside this scroller, so the dialog
          itself never gets a second scrollbar. */}
      {open ? (
        <div className="relative max-h-[inherit] overflow-y-auto p-3 sm:p-4">{children}</div>
      ) : null}
    </dialog>
  );
}

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** right: desktop detail; bottom: mobile sheet; full: mobile full-screen form. */
  side?: "right" | "bottom" | "full";
  /** When true, closing asks before discarding unsaved changes (in-app dialog). */
  dirty?: boolean;
  /** Header buttons beside the close button (icon buttons, ghost). */
  actions?: ReactNode;
  footer?: ReactNode;
  children: ReactNode;
  "data-testid"?: string;
}

export function Drawer({
  open,
  onClose,
  title,
  side = "right",
  dirty = false,
  actions,
  footer,
  children,
  "data-testid": testId,
}: DrawerProps) {
  const [confirming, setConfirming] = useState(false);
  const requestClose = () => {
    if (dirty) setConfirming(true);
    else onClose();
  };
  const ref = useModal(open, requestClose, () => true);
  const titleId = useId();
  return (
    <dialog
      ref={ref}
      className="ady-drawer"
      data-side={side}
      aria-labelledby={titleId}
      data-testid={testId}
      onClick={(e) => {
        // Scrim click closes (unless dirty → ask).
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      {open ? (
        <div className="flex h-full max-h-[inherit] flex-col">
          {side === "bottom" ? (
            <button
              type="button"
              onClick={requestClose}
              className="mx-auto flex min-h-11 w-24 items-center justify-center"
              aria-label={t("drawer.handleLabel")}
            >
              <span className="h-1 w-10 rounded-full bg-line" />
            </button>
          ) : null}
          <header className="flex items-center gap-4 border-b border-subtle px-5 py-3">
            <h2 id={titleId} className="flex-1 text-title-sm font-semibold">
              {title}
            </h2>
            {actions}
            <Button
              variant="ghost"
              size="md"
              iconOnly
              icon="x"
              onClick={requestClose}
              className="text-fg-secondary"
            >
              {t("drawer.closeLabel")}
            </Button>
          </header>
          <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
          {footer ? (
            <footer className="border-t border-subtle px-5 py-3 shadow-1">{footer}</footer>
          ) : null}
        </div>
      ) : null}
      <Dialog
        open={confirming}
        onClose={() => setConfirming(false)}
        title={t("dialog.unsavedTitle")}
        description={t("dialog.unsavedBody")}
        dismissLabel={t("dialog.unsavedKeep")}
        size="sm"
        confirm={{
          label: t("dialog.unsavedDiscard"),
          variant: "danger",
          onClick: () => {
            setConfirming(false);
            onClose();
          },
        }}
      />
    </dialog>
  );
}

/** Inline icon used by confirm copy. */
export function DialogIcon({ tone }: { tone: "danger" | "warning" }) {
  return (
    <Icon
      name={tone === "danger" ? "octagon" : "triangle"}
      className={tone === "danger" ? "text-danger-fg" : "text-warning-fg"}
    />
  );
}
